import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n, useUnavailableStatus } from 'contexts/AuthContext'
import { useUserProfileStatus } from 'hooks/useUserProfileStatus'
import {
  completeOnboardingV2Promise,
  getRehabProgramV2Promise,
  joinSkuProgramsPromise,
  updateUserPromise,
} from 'apis/FitOnCareAPI'
import { ScreenKey } from 'views/Care/util'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { logBrazeEvent } from 'utils/brazeInit'
import { rehabCareFlowFinished } from '../util'
import MultipleProgramContent from './MultipleProgramContent'
import _ from 'lodash'
import { LoadingContent } from 'components/LoadingDialog/index'
import ContentHeader from 'views/Care/ContentHeader'
import { useApplyNewPatientTag } from 'hooks/useApplyTags'
import { getLocalLanguage } from 'utils/authLocalStorage'
import { UnavailableStatus } from 'views/Care/Unavailable'
import { useAssessmentFlag } from 'contexts/FeatureFlagContext'

const MultipleThemes = (props) => {
  const { sku, fitonUserId, programId, from, setCurrentKey, themes } = props

  const { i18n } = useI18n()
  const navigate = useNavigate()

  const [subCategoryPrograms, setSubCategoryPrograms] = useState([])
  const [currentContentIndex, setCurrentContentIndex] = useState(0)
  const { getUserProfileStatus } = useUserProfileStatus()
  const [selectedPrograms, setSelectedPrograms] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { applyNewPatientTag } = useApplyNewPatientTag()
  const { setUnavailableStatus } = useUnavailableStatus()
  const hasCompletedOnboarding = useRef(false)

  const { showAssessment } = useAssessmentFlag()

  const selectedThemes = useMemo(() => {
    return !_.isEmpty(themes) ? themes : []
  }, [themes])

  const { allThemeNames, fitonItemIds } = useMemo(() => {
    return {
      allThemeNames: selectedThemes?.map((theme) => theme.name).join(', '),
      fitonItemIds: selectedThemes?.map((theme) => theme.fitonItemId).join(','),
    }
  }, [selectedThemes])

  const getProgram = useCallback(async () => {
    if (fitonUserId) {
      const data = await getRehabProgramV2Promise({
        userId: fitonUserId,
        programId,
        subCategoryIds: fitonItemIds,
      })
      const programs = data?.subcategoryPrograms || []
      setSubCategoryPrograms(programs)
      setIsLoading(false)
    }
  }, [fitonUserId, programId, fitonItemIds])

  useEffect(() => {
    getProgram()
  }, [getProgram])

  const logEvent = useCallback(async () => {
    const { shouldSkipFlow } = await getUserProfileStatus()
    logAmplitudeEvent('Screen View: Onboarding - Signup Success', {
      sku: sku,
      theme: allThemeNames,
      ...(shouldSkipFlow ? { flow_skip: 1 } : {}),
      language: getLocalLanguage(),
      ...(sku === 'mca'
        ? {
            tracks: ['msk'],
            body_areas: themes?.map((area) => area.name).join(','),
          }
        : {}),
    })
    updateUserPromise({
      skuStatus: {
        sku: sku === 'mca' ? 'msk' : sku,
        status: 2,
      },
    })
    logBrazeEvent(`User Approved - ${sku}`)
  }, [getUserProfileStatus, sku, allThemeNames, themes])

  const applyTags = useCallback(async () => {
    const { shouldSkipFlow } = await getUserProfileStatus()
    if (!shouldSkipFlow) {
      applyNewPatientTag()
    }
  }, [applyNewPatientTag, getUserProfileStatus])

  const completeOnboarding = useCallback(
    async ({ fitonUserId, themeIds }) => {
      const resp = await completeOnboardingV2Promise({
        userId: fitonUserId,
        themeIds,
      })
      if (resp.code === 200) {
        console.log('completeOnboarding success')
        applyTags()
      } else {
        console.log('completeOnboarding failed')
      }
      logEvent()
    },
    [applyTags, logEvent]
  )

  useEffect(() => {
    const fitonItemIds = selectedThemes?.map((theme) => theme.fitonItemId)
    if (
      fitonItemIds?.length &&
      fitonUserId &&
      !hasCompletedOnboarding.current
    ) {
      hasCompletedOnboarding.current = true
      completeOnboarding({
        fitonUserId: fitonUserId,
        themeIds: fitonItemIds,
      })
    }
  }, [selectedThemes, fitonUserId])

  const joinProgram = async ({ fitonUserId, sku, programs }) => {
    if (!programs.length) {
      return
    }

    const resp = await joinSkuProgramsPromise({
      userId: fitonUserId,
      sku: sku === 'mca' ? 'msk' : sku,
      programIds: programs.map((program) => program.id),
      isMca: sku === 'mca',
    })
    if (resp.code === 200) {
      onFlowFinished(programs[0].id || programId)
    } else {
      setUnavailableStatus(UnavailableStatus.NOT_ELIGIBLE_CONGRATULATIONS)
      setCurrentKey(ScreenKey.UNAVAILABLE)
    }
  }

  const onFlowFinished = (programId) => {
    if (sku === 'mca') {
      logAmplitudeEvent('Click: Onboarding - Signup Success - Continue', {
        sku,
        destination: 'msk',
      })
      if (showAssessment) {
        navigate(
          `/medicare-access/assessment?fitonUserId=${fitonUserId}&variant=${sku}`
        )
        return
      }
    }
    rehabCareFlowFinished(true, programId || 0, from, fitonUserId, sku)
  }

  const onNext = useCallback(
    (index, program) => {
      const themeId = subCategoryPrograms[index].subCategoryId
      const existingProgramIndex = selectedPrograms.findIndex(
        (p) => p.themeId === themeId
      )

      let newPrograms
      if (existingProgramIndex !== -1) {
        newPrograms = [...selectedPrograms]
        newPrograms[existingProgramIndex] = {
          ...program,
          themeId: themeId,
        }
      } else {
        newPrograms = [...selectedPrograms, { ...program, themeId: themeId }]
      }

      setSelectedPrograms(newPrograms)
      const nextIndex = index + 1
      if (nextIndex < subCategoryPrograms.length) {
        setCurrentContentIndex(nextIndex)
      } else {
        joinProgram({
          programs: newPrograms,
          fitonUserId: fitonUserId,
          sku,
        })
      }
    },
    [subCategoryPrograms, fitonUserId, sku, selectedPrograms]
  )

  const onBack = (e) => {
    if (currentContentIndex <= 0) {
      props.setRemoveEventListener(false)
      props.onBack(e)
    } else {
      setCurrentContentIndex(currentContentIndex - 1)
    }
  }

  const themeContents = useMemo(() => {
    if (_.isEmpty(subCategoryPrograms)) {
      return []
    }
    const contents = subCategoryPrograms.map((theme, index) => {
      return (
        <MultipleProgramContent
          i18n={i18n}
          theme={theme}
          index={index}
          onNext={onNext}
          isLast={index === subCategoryPrograms.length - 1}
          themeSize={subCategoryPrograms.length}
          selectedPrograms={selectedPrograms}
          sku={sku}
        />
      )
    })
    return contents
  }, [subCategoryPrograms, onNext, selectedPrograms, sku])

  useEffect(() => {
    if (themeContents.length > 0) {
      setCurrentContentIndex(0)
    }
  }, [themeContents.length])

  return (
    <>
      <ContentHeader {...props} onBack={onBack} />
      <div className='info-content'>
        {isLoading ? (
          <div className='loading-content'>
            <LoadingContent />
          </div>
        ) : (
          themeContents[currentContentIndex]
        )}
      </div>
    </>
  )
}

export default MultipleThemes
